import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  ListItemText,
  CircularProgress,
  Typography,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ProjectApi } from '../../apis';

// Styled components for the checkbox
const CustomCheckbox = styled(Checkbox)(({ theme }) => ({
  color: theme.palette.primary.main,
  '&.Mui-checked': {
    color: theme.palette.primary.main,
  },
}));

// Styled components for dialog actions
const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 3),
}));

const CancelButton = styled(StyledButton)(({ theme }) => ({
  backgroundColor: '#d53a3a',
  color: 'white',
  '&:hover': {
    backgroundColor: '#b32e2e',
  },
}));

const AssignButton = styled(StyledButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

function AssignProjectsDialog({ open, handleClose, selectedProjects = [], onAssign, type, entityId }) {
  const [selected, setSelected] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchProjects();
      // Sync the selected projects with props when dialog opens
      setSelected(selectedProjects.map(p => p.projectId));
    }
  }, [open, selectedProjects]);

  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ProjectApi.getAllProjects();
      setProjects(res.data.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again later.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = useCallback((projectId) => {
    setSelected((prevSelected) => {
      const currentIndex = prevSelected.indexOf(projectId);
      const newSelected = [...prevSelected];

      if (currentIndex === -1) {
        newSelected.push(projectId);
      } else {
        newSelected.splice(currentIndex, 1);
      }

      return newSelected;
    });
  }, []);

  const handleAssign = () => {
    // Prepare the selected projects for assignment
    onAssign(projects.filter(project => selected.includes(project.id)));
    handleClose();
  };

  const handleCheckboxClick = (event, projectId) => {
    event.stopPropagation(); // Prevent the click event from bubbling up
    handleToggle(projectId);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" color="primary" fontWeight="bold" ml={1}>
          Assign Projects
        </Typography>
      </DialogTitle>
      <Divider sx={{ width: "90%", mx: "auto" }} />
      <DialogContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
            <CircularProgress color="primary" />
          </div>
        ) : error ? (
          <Typography variant="body2" color="error" align="center">
            {error}
          </Typography>
        ) : projects.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center">
            No projects available for assignment.
          </Typography>
        ) : (
          <List>
            {projects.map((project) => (
              <ListItem key={project.id}>
                <ListItemText primary={project.name} />
                <FormControlLabel
                  control={
                    <CustomCheckbox
                      checked={selected.indexOf(project.id) !== -1}
                      onClick={(event) => handleCheckboxClick(event, project.id)}
                    />
                  }
                  label=""
                  sx={{ marginLeft: 2 }}
                />
              </ListItem>
            ))}
          </List>
        )}
        <DialogActions>
          <CancelButton onClick={handleClose}>
            Cancel
          </CancelButton>
          <AssignButton onClick={handleAssign} disabled={selected.length === 0}>
            Assign
          </AssignButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

export default AssignProjectsDialog;
